import apiClient from './apiClient.js';

/**
 * Get current user's subscription status
 * @returns {Promise<object>} Subscription data
 */
export const getSubscriptionStatus = async () => {
    const url = '/subscription/status';
    try {
        const response = await apiClient.get(url);
        return response.data;
    } catch (err) {
        console.error('Error fetching subscription status:', err);
        throw err;
    }
};

/**
 * Create a Paddle checkout session
 * @param {string} priceId - Optional price ID (uses default if not provided)
 * @returns {Promise<object>} Checkout session data with transactionId
 */
export const createCheckoutSession = async (priceId = null) => {
    const url = '/subscription/create-checkout';
    try {
        const response = await apiClient.post(url, priceId ? { priceId } : {});
        return response.data;
    } catch (err) {
        console.error('Error creating checkout session:', err);
        throw err;
    }
};

/**
 * Cancel current subscription
 * @returns {Promise<object>} Cancellation result
 */
export const cancelSubscription = async () => {
    const url = '/subscription/cancel';
    try {
        const response = await apiClient.post(url, {});
        return response.data;
    } catch (err) {
        console.error('Error canceling subscription:', err);
        throw err;
    }
};

/**
 * Get update payment method URL
 * @returns {Promise<object>} Update payment URL
 */
export const getUpdatePaymentUrl = async () => {
    const url = '/subscription/update-payment';
    try {
        const response = await apiClient.get(url);
        return response.data;
    } catch (err) {
        console.error('Error getting update payment URL:', err);
        throw err;
    }
};

/**
 * Initialize Paddle.js with client-side token
 * Call this once when your app loads
 */
export const initializePaddle = () => {
    if (typeof window === 'undefined' || !window.Paddle) {
        console.warn('Paddle.js not loaded');
        return false;
    }

    // Get Paddle configuration from .env
    const environment = process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox';
    const clientToken = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;

    if (!clientToken) {
        console.error('REACT_APP_PADDLE_CLIENT_TOKEN not configured in .env');
        return false;
    }

    try {
        window.Paddle.Environment.set(environment);
        window.Paddle.Initialize({
            token: clientToken,
        });
        console.log(`✅ Paddle initialized (${environment})`);
        return true;
    } catch (error) {
        console.error('Error initializing Paddle:', error);
        return false;
    }
};

/**
 * Open Paddle checkout overlay
 * @param {string} transactionId - Transaction ID from backend
 * @param {Function} onSuccess - Callback function when payment succeeds
 * @returns {Promise<void>}
 */
export const openPaddleCheckout = (transactionId, onSuccess) => {
    if (!window.Paddle) {
        throw new Error('Paddle.js not loaded');
    }

    return window.Paddle.Checkout.open({
        transactionId: transactionId,
        settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: 'en',
            successUrl: window.location.href, // Stay on same page
        },
        eventCallback: (event) => {
            console.log('Paddle event:', event);

            // Handle checkout completion
            if (event.name === 'checkout.completed') {
                console.log('✅ Payment completed!');
                if (onSuccess) {
                    onSuccess(event.data);
                }
            }

            // Handle checkout close
            if (event.name === 'checkout.closed') {
                console.log('Checkout closed');
            }
        },
    });
};

